/* ============================================================
 * 首页聚合 store
 *
 * 5 分钟内复用 `/app/home/overview` 结果，让 HomeView / HallView /
 * LoginView 共享同一份缓存，避免每个公开页都各自拉一遍。
 *
 * 用法：
 *   const home = useHomeStore()
 *   onMounted(() => void home.load())
 *   home.data?.kpi.creatorCount
 *
 * 失效策略：
 *   - 首次或缓存缺失（null / 超过 5 分钟） → 拉取
 *   - 5 分钟内再次调用 → 直接返回缓存（并发去重由 loading guard 保证）
 *   - load(true) → 强制刷新（用于重试按钮）
 *   - 失败保留旧 data（fail-soft），不闪烁空态
 *
 * HomeView 在 onBeforeUnmount 调用 reset() 清缓存以便下次进入重拉；
 * HallView / LoginView 不调用 reset（共享缓存的意义）。
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getHomeOverview } from '@/api/home'
import type { HomeOverview } from '@/types/home'

const CACHE_TTL_MS = 5 * 60 * 1000

export const useHomeStore = defineStore('home', () => {
  const data = ref<HomeOverview | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchedAt = ref(0)

  const isStale = computed(() => Date.now() - lastFetchedAt.value > CACHE_TTL_MS)

  async function load(force = false): Promise<void> {
    if (loading.value) return
    if (data.value && !force && !isStale.value) return
    loading.value = true
    error.value = null
    try {
      data.value = await getHomeOverview()
      lastFetchedAt.value = Date.now()
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载失败，请稍后重试'
    } finally {
      loading.value = false
    }
  }

  function reset(): void {
    data.value = null
    loading.value = false
    error.value = null
    lastFetchedAt.value = 0
  }

  return { data, loading, error, lastFetchedAt, isStale, load, reset }
})